import { useMemo } from "react";
import { Card, Divider, Empty, Tag, Typography } from "antd";
import keyBy from "lodash.keyby";
import { useTranslation } from "react-i18next";

import {
  type ClientTrainingProgram,
  type ClientTrainingProgramDetails,
  type ExerciseInWorkout,
  type ProgramWorkout,
  type WeekDay,
} from "@fitness-app/data-models/entities/TrainingProgram";

import ExerciseInfo from "~/components/ExerciseInfo/ExerciseInfo";

const weekdays: WeekDay[] = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

interface ArchivedProgramScheduleProps {
  program: ClientTrainingProgram;
  details: ClientTrainingProgramDetails | null;
}

/**
 * Nazwa w jednej linii, parametry w drugiej.
 *
 * Dawny popover treningu w kalendarzu układał checkbox, nazwę i wszystkie `<span>`-y
 * `ExerciseInfo` w jednym `flex`-ie. W wąskim popoverze to działało, ale w siatce kart
 * długa nazwa („Wypychanie bioder leżąc, stopy na ławce") wypycha liczby do kolejnych
 * wierszy i każdy token — `4`, `s.`, `|`, `15`, `powt.` — łamie się osobno.
 *
 * Nie ma tu też checkboxa. `client_training_program_details` trzyma szablon rozpiski, a nie
 * zapis wykonania — wszystkie pozycje wyszłyby odhaczone na nie, co czytałoby się jak
 * „klientka nic nie zrobiła". Co faktycznie zrobiła, widać w kalendarzu i w dzienniku.
 */
const ExerciseRow = ({ exercise }: { exercise: ExerciseInWorkout }) => (
  <li className="py-1">
    <Typography.Text>{exercise.exercise.name}</Typography.Text>
    <div className="flex flex-wrap items-center gap-x-1.5 text-xs text-gray-500">
      <ExerciseInfo exerciseInWorkout={exercise} />
    </div>
  </li>
);

const ExerciseSection = ({
  exercises,
  label,
  hideEmpty = false,
}: {
  exercises: ExerciseInWorkout[];
  label: string;
  hideEmpty?: boolean;
}) => {
  if (hideEmpty && !exercises.length) {
    return null;
  }

  return (
    <>
      <Divider plain style={{ margin: "8px 0" }}>
        <span className="text-xs text-gray-400">{label}</span>
      </Divider>
      <ul className="divide-y divide-gray-100">
        {exercises.map((exercise) => (
          <ExerciseRow key={exercise.id} exercise={exercise} />
        ))}
      </ul>
    </>
  );
};

const WorkoutPreview = ({ workout }: { workout: ProgramWorkout }) => {
  const allExercises = [...workout.preWorkout, ...workout.exercises, ...workout.postWorkout];

  if (!allExercises.length) {
    return <Empty imageStyle={{ height: 30 }} image={Empty.PRESENTED_IMAGE_SIMPLE} description="Brak ćwiczeń" />;
  }

  return (
    <>
      <ExerciseSection exercises={workout.preWorkout} label="Rozgrzewka" hideEmpty />
      <ExerciseSection exercises={workout.exercises} label="Trening" />
      <ExerciseSection exercises={workout.postWorkout} label="Po treningu" hideEmpty />
    </>
  );
};

/**
 * TRE-1833 — rozpiska planu archiwalnego, wyłącznie do odczytu.
 *
 * Świadomie NIE jest to `TraineeProgramBuilder` ani jego tryb read-only. Builder jest
 * zbudowany wokół edycji aktywnego planu — dodawania i usuwania treningów, przestawiania
 * ćwiczeń, zapisu do `client_training_program_details` — więc doklejenie do niego flagi
 * „tylko podgląd" zostawiłoby całą tę maszynerię o jedno `if` od planu zamkniętego.
 * Tutaj nie ma czym niczego zapisać.
 */
export const ArchivedProgramSchedule = ({ program, details }: ArchivedProgramScheduleProps) => {
  const { t } = useTranslation(["common"]);

  const week = useMemo(() => {
    const workoutsById = keyBy(details?.workouts ?? [], "id");
    // Trening usunięty z planu w trakcie jego trwania ląduje w `archivedWorkouts`, ale
    // harmonogram nadal się do niego odwołuje — bez tego dzień wyglądałby na wolny.
    const archivedWorkoutsById = keyBy(details?.archivedWorkouts ?? [], "id");

    return weekdays.map((day) => {
      const workoutId = program.workoutsSchedule?.schedule[0]?.[day]?.workoutDay ?? null;
      const workout: ProgramWorkout | null = workoutId
        ? (workoutsById[workoutId] ?? archivedWorkoutsById[workoutId] ?? null)
        : null;

      return { day, workout };
    });
  }, [details, program]);

  if (!program.workoutsSchedule) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Ten plan nie ma zapisanego harmonogramu" />;
  }

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      {week.map(({ day, workout }) => (
        <Card
          key={day}
          size="small"
          title={
            <div className="flex min-w-0 items-center justify-between gap-x-2">
              <Typography.Text strong>{t(`common:weekdays.${day}`)}</Typography.Text>
              {workout ? (
                <Tag color="default" className="shrink-0 truncate">
                  {workout.name}
                </Tag>
              ) : null}
            </div>
          }
        >
          {workout ? (
            <WorkoutPreview workout={workout} />
          ) : (
            <Empty imageStyle={{ height: 30 }} image={Empty.PRESENTED_IMAGE_SIMPLE} description="Dzień wolny" />
          )}
        </Card>
      ))}
    </div>
  );
};

export default ArchivedProgramSchedule;
